import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { ButtonWrapper } from 'foundations-components/transitional/components/ButtonWrapper';
import { trackWACompose } from '../utils/WATracker';
import AccountDisabledAlert from './AccountDisabledAlert';
import { NewConversationButton, SendTemplateButton, UpgradeButton, ViewConversationButton } from './SendActionButtons';
import { SendingState } from '../types/SendingState';
import { Link } from 'foundations-components/transitional/components/Link';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const LEARN_MORE_URL = 'https://www.whatsapp.com/legal/business-policy/#:~:text=practices%20suggested%20below.-,Acceptable%20Message%20Types,-%3A';
const makeSendingBanner = (message, buttons) => {
  return /*#__PURE__*/_jsxs(Alert, {
    type: "warning",
    marginInline: "500",
    marginBlockStart: "500",
    paddingBlock: "500",
    children: [/*#__PURE__*/_jsx("div", {
      children: message
    }), /*#__PURE__*/_jsx(ButtonWrapper, {
      marginBlockStart: "200",
      children: buttons
    })]
  });
};
const determineSendingState = (recipient, sender, threadDetails) => {
  if (sender && recipient) {
    if (threadDetails) {
      if (threadDetails.usage >= threadDetails.limit) {
        return SendingState.LIMIT_REACHED;
      }
      if (threadDetails.details) {
        const isClosed = threadDetails.details.status === 'ENDED';
        const within24hr = Date.now() - threadDetails.details.latestReceivedTimestamp < 86400000;
        const contactNotResponded = !threadDetails.details.latestReceivedTimestamp;
        if (!isClosed) {
          if (contactNotResponded) {
            return SendingState.CONTACT_NOT_RESPONDED;
          }
          return within24hr ? SendingState.OPEN_CONVERSATION_WITHIN_24HR : SendingState.OPEN_CONVERSATION_PAST_24HR;
        } else {
          return within24hr ? SendingState.CLOSED_CONVERSATION_WITHIN_24HR : SendingState.CLOSED_CONVERSATION_PAST_24HR;
        }
      }
    }
  }
  return SendingState.NEW_CONVERSATION;
};
export const SendingAlert = ({
  inboxId,
  openIframe,
  isSubscribedForTemplates,
  recipient,
  sender,
  threadDetails
}) => {
  const status = determineSendingState(recipient, sender, threadDetails);
  const threadId = threadDetails && threadDetails.threadId;
  switch (status) {
    case SendingState.NEW_CONVERSATION:
      {
        const isMissingSenderOrRecipient = !sender || !recipient;
        const disabledReason = isMissingSenderOrRecipient ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "composer-ui.whatsapp.disableReasons.noSenderOrRecipient"
        }) : !isSubscribedForTemplates ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "composer-ui.whatsapp.disableReasons.notOptedIn"
        }) : undefined;
        return makeSendingBanner( /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "composer-ui.whatsapp.sendActions.newConversation_jsx",
          elements: {
            Link
          },
          options: {
            href: LEARN_MORE_URL,
            target: '_blank',
            external: true
          }
        }), /*#__PURE__*/_jsx(SendTemplateButton, {
          openIframe: openIframe,
          disabledReason: disabledReason
        }));
      }
    case SendingState.CONTACT_NOT_RESPONDED:
      return makeSendingBanner( /*#__PURE__*/_jsx(FormattedMessage, {
        message: "composer-ui.whatsapp.sendActions.contactNotResponded"
      }), /*#__PURE__*/_jsx(ViewConversationButton, {
        threadId: threadId
      }));
    case SendingState.LIMIT_REACHED:
      return makeSendingBanner( /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "composer-ui.whatsapp.sendActions.limitReached_jsx",
        elements: {
          strong: 'strong'
        }
      }), /*#__PURE__*/_jsx(UpgradeButton, {}));
    case SendingState.SENDER_UNAVAILABLE:
      return /*#__PURE__*/_jsx(AccountDisabledAlert, {
        inboxId: inboxId
      });
    case SendingState.OPEN_CONVERSATION_WITHIN_24HR:
      return makeSendingBanner( /*#__PURE__*/_jsx(FormattedMessage, {
        message: "composer-ui.whatsapp.sendActions.openConvWithin24h"
      }), /*#__PURE__*/_jsx(ViewConversationButton, {
        threadId: threadId,
        track: () => trackWACompose('view full conversation within 24hr')
      }));
    case SendingState.OPEN_CONVERSATION_PAST_24HR:
      return makeSendingBanner( /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "composer-ui.whatsapp.sendActions.openConvPast24h_jsx",
        elements: {
          Link
        },
        options: {
          href: LEARN_MORE_URL,
          target: '_blank',
          external: true
        }
      }), /*#__PURE__*/_jsx(ViewConversationButton, {
        threadId: threadId,
        track: () => trackWACompose('view full conversation after 24hr')
      }));
    case SendingState.CLOSED_CONVERSATION_WITHIN_24HR:
      return makeSendingBanner( /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "composer-ui.whatsapp.sendActions.closedConvWithin24h_jsx",
        elements: {
          Link
        },
        options: {
          href: LEARN_MORE_URL,
          target: '_blank',
          external: true
        }
      }), /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(ViewConversationButton, {
          threadId: threadId
        }), /*#__PURE__*/_jsx(NewConversationButton, {
          openIframe: openIframe,
          track: () => trackWACompose('Start new conversation within 24hr')
        })]
      }));
    case SendingState.CLOSED_CONVERSATION_PAST_24HR:
      return makeSendingBanner( /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "composer-ui.whatsapp.sendActions.closedConvPast24h_jsx",
        elements: {
          Link
        },
        options: {
          href: LEARN_MORE_URL,
          target: '_blank',
          external: true
        }
      }), /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(ViewConversationButton, {
          threadId: threadId
        }), /*#__PURE__*/_jsx(NewConversationButton, {
          openIframe: openIframe
        })]
      }));
    default:
      return null;
  }
};
SendingAlert.displayName = 'SendingAlert';